import React, { useEffect, useState } from "react";
import { Plus, Edit, Trash2, RefreshCw, X } from "lucide-react";
import { useGeneric } from "../../hooks/useGeneric";

const EMPTY_FORM = {
    group_id: null,
    group_name: "",
};

const ProductGroup = () => {
    const [form, setForm] = useState(EMPTY_FORM);
    const [showForm, setShowForm] = useState(false);
    const [errors, setErrors] = useState({});
    const [search, setSearch] = useState("");
    const [saving, setSaving] = useState(false);

    const {
        data: productgroups = [],
        loading,
        fetchAll: fetchProductGroups,
        save: saveProductGroup,
        remove: deleteProductGroup,
    } = useGeneric({
        tableName: "productgroupmaster",
        idColumn: "group_id",
        orderBy: "group_name",
        removeOnInsert: ["group_id"],
    });

    useEffect(() => {
        fetchProductGroups();
    }, [fetchProductGroups]);

    const set = (key, val) => {
        setForm((prev) => ({ ...prev, [key]: val }));
        setErrors((prev) => ({ ...prev, [key]: "" }));
    };

    // ── Validation ───────────────────────────────────────────────────
    const validate = () => {
        const errs = {};

        if (!(form.group_name ?? "").trim())
            errs.group_name = "ProductGroup name is required.";
        else if (form.group_name.length > 50)
            errs.group_name = "Max 50 characters.";

        setErrors(errs);
        return Object.keys(errs).length === 0;
    };

    // ── Save ─────────────────────────────────────────────────────────
    const onSave = async () => {
        if (!validate()) return;
        setSaving(true);
        try {
            await saveProductGroup(form);
            closeModal();
        } catch (err) {
            console.error("Save failed", err);
            alert("Save failed: " + (err.message || "Unknown error"));
        } finally {
            setSaving(false);
        }
    };

    // ── Modal helpers ────────────────────────────────────────────────
    const openAdd = () => {
        setForm(EMPTY_FORM);
        setErrors({});
        setShowForm(true);
    };

    const openEdit = (productgroup) => {
        setForm({ ...productgroup }); // ✅ fixed: spread the selected productgroup, not the whole array
        setErrors({});
        setShowForm(true);
    };

    const closeModal = () => {
        setShowForm(false);
        setForm(EMPTY_FORM);
        setErrors({});
    };

    // ── Delete ───────────────────────────────────────────────────────
    const handleDelete = (id) => {
        if (window.confirm("Are you sure you want to delete this product group?")) {
            deleteProductGroup(id);
        }
    };

    // ── Filter ───────────────────────────────────────────────────────
    const filtered = (productgroups || [])
        .filter((u) =>
            u.group_name?.toLowerCase().includes(search.toLowerCase())
        )
        .sort((a, b) => a.group_name?.localeCompare(b.group_name));

    return (
        <div className="cl-page">
            {/* ── Panel ── */}
            <div className="cl-panel">
                <div className="cl-panel-heading">
                    <span>Product Group Details</span>
                    <button className="cl-add-btn" onClick={openAdd} title="Add ProductGroup">
                        <Plus size={16} />
                    </button>
                </div>

                {/* Search bar */}
                <div className="cl-search-bar">
                    <input
                        type="text"
                        placeholder="Search product groups…"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="cl-search-input"
                    />
                    <button className="cl-refresh-btn" onClick={fetchProductGroups} title="Refresh">
                        <RefreshCw size={15} />
                    </button>
                </div>

                {/* Table */}
                {loading ? (
                    <div className="cl-loading">Loading…</div>
                ) : (
                    <div className="cl-table-wrapper">
                        <table className="cl-table">
                            <thead>
                                <tr>
                                    <th>ID</th>
                                    <th>Group Name</th>
                                    <th className="cl-actions-th">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.length === 0 ? (
                                    <tr>
                                        <td colSpan={3} className="cl-empty">
                                            No Product Groups found.
                                        </td>
                                    </tr>
                                ) : (
                                    filtered.map((u, i) => (
                                        <tr
                                            key={u.group_id ?? i}
                                            className={i % 2 === 0 ? "cl-row-even" : "cl-row-odd"}
                                        >
                                            <td data-label="ID">{u.group_id}</td>
                                            <td data-label="Group Name">{u.group_name}</td>
                                            <td className="cl-actions-td">
                                                <button
                                                    className="cl-icon-btn cl-edit"
                                                    onClick={() => openEdit(u)}
                                                    title="Edit"
                                                >
                                                    <Edit size={15} />
                                                </button>
                                                <button
                                                    className="cl-icon-btn cl-delete"
                                                    onClick={() => handleDelete(u.group_id)}
                                                    title="Delete"
                                                >
                                                    <Trash2 size={15} />
                                                </button>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                )}

                <div className="cl-panel-footer" />
            </div>

            {/* ── Modal ── */}
            {showForm && (
                <div className="cl-modal-backdrop">
                    <div className="cl-modal-overlay" />
                    <div className="cl-modal" onClick={(e) => e.stopPropagation()}>

                        {/* Modal Header */}
                        <div className="cl-modal-header">
                            <h4 className="cl-modal-title">
                                {form.group_id ? "Edit Product Group" : "Add Product Group"}
                            </h4>
                            <button className="cl-modal-close" onClick={closeModal}>
                                <X size={18} />
                            </button>
                        </div>

                        {/* Modal Body */}
                        <div className="cl-modal-body">
                            <form autoComplete="off">
                                <div className="cl-form-group cl-row">
                                    <div className="cl-col-10">
                                        <label className="cl-control-label">Group Name</label>
                                        <input
                                            type="text"
                                            className={`cl-form-control${errors.group_name ? " cl-input-error" : ""}`}
                                            placeholder="Product Group Name"
                                            maxLength={50}
                                            value={form.group_name}
                                            onChange={(e) => set("group_name", e.target.value)}
                                        />
                                        {errors.group_name && (
                                            <span className="cl-error-msg">{errors.group_name}</span>
                                        )}
                                    </div>
                                    <div className="cl-col-2">
                                        <label className="cl-control-label">ID</label>
                                        <input
                                            type="text"
                                            className="cl-form-control cl-readonly"
                                            placeholder="Auto"
                                            value={form.group_id ?? ""}
                                            readOnly
                                        />
                                    </div>
                                </div>
                            </form>
                        </div>

                        {/* Modal Footer */}
                        <div className="cl-modal-footer">
                            <button className="cl-btn cl-btn-default" onClick={closeModal}>
                                Close
                            </button>
                            <button
                                className="cl-btn cl-btn-primary"
                                onClick={onSave}
                                disabled={saving}
                            >
                                {saving ? "Saving…" : form.group_id ? "Update" : "Add"}
                            </button>
                        </div>

                    </div>
                </div>
            )}
        </div>
    );
};

export default ProductGroup;